import * as React from "react";
import { styled } from "@mui/material/styles";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ClearIcon from "@mui/icons-material/Clear";
import { createGlobalStyle } from "styled-components";
import "primereact/resources/themes/saga-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import { ProductIcon } from "./icons";
import { useStyle } from "./style";
import CheckIcon from "@mui/icons-material/Check";
import { Tooltip, tooltipClasses, TooltipProps } from "@mui/material";
import { TreeSelect, TreeSelectProps } from "antd";

/** Deep-clone tree nodes so parent rows stay checkable in multi-select mode (API data sets selectable: false on groups). */
const withSelectableForMultiCheck = (nodes: any[] | undefined): any[] => {
  if (!nodes?.length) return [];
  return nodes.map((n) => ({
    ...n,
    selectable: true,
    children: n.children?.length
      ? withSelectableForMultiCheck(n.children)
      : undefined,
  }));
};
import { UnderlinedTab, UnderlinedTabs } from "@/components/tabs/primary-tabs";
import { ETab } from "@/pages-components/admin/home/widgets/quote-widget/enums";
import { ProductCategoryType, isCustomerSkuProduct } from "@/pages-components/admin/home/widgets/quote-widget/states";
import { useTheme } from "@/contexts/theme";

// Styled components for TreeSelect and Clear Button
const StyledTreeSelect = styled<any>(TreeSelect)(
  ({ selectedOption, isRequired, dir, hasValue }: any) => ({
    boxSizing: "border-box",
    borderRadius: "var(--theme-dropdown-border-radius, 4px)",
    height: "40px",
    fontSize: 14,
    display: "flex",
    alignItems: "center",
    color: "#000000",
    backgroundColor: "#FFF",
    boxShadow: "var(--theme-dropdown-shadow, 0px 1px 10px rgba(0, 0, 0, 0.08))",
    fontFamily: "Heebo",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
    border: "none !important", // Remove wrapper border to prevent double border
    borderBottom: !isRequired
      ? "unset !important"
      : selectedOption
        ? "2px solid rgb(67,195,232) !important"
        : "2px solid rgb(237, 2, 140) !important",
    "& .ant-select-selector": {
      border: "1px solid var(--theme-dropdown-border-color, transparent) !important",
      borderRadius: "var(--theme-dropdown-border-radius, 4px) !important",
      display: "flex !important",
      alignItems: "center !important",
    },
    "&.ant-select-focused .ant-select-selector": {
      borderColor: "var(--theme-dropdown-border-color, transparent) !important",
      borderWidth: "1px !important",
      borderStyle: "solid !important",
      boxShadow:
        "var(--theme-dropdown-shadow, 0 0 0 2px rgba(224, 224, 224, 0.2)) !important",
    },
    "& .p-dropdown-label": {
      padding: 0,
      direction: "ltr",
      fontFamily: "Heebo",
      fontStyle: "normal",
      fontWeight: 500,
    },
    "& .ant-select-dropdown": {
      zIndex: 999999999999,
    },
    "& .p-tree-container": {
      width: "100%",
    },
    "& .p-dropdown": {
      height: "40px",
      fontFamily: "Heebo",
      fontStyle: "normal",
      fontWeight: 500,
      width: "100%",
    },
    "& .p-treeselect-label-container": {
      fontFamily: "Lexend",
      fontStyle: "normal",
      fontWeight: 400,
      fontSize: "16px",
      padding: "4px 10px",
    },
    "& .ant-select-selection-placeholder": {
      color: "var(--theme-dropdown-placeholder-color, #B1B1B1) !important",
      opacity: "1 !important",
      fontFamily: "Heebo !important",
      fontStyle: "normal !important",
      fontWeight: "var(--theme-dropdown-placeholder-weight, 500) !important",
      fontSize: "var(--theme-dropdown-placeholder-size, 12px) !important",
    },
    "& .p-treeselect-trigger-icon": {
      display: "none",
    },
    "& .ant-select-clear": {
      ...(dir === "rtl"
        ? { left: "8px", right: "auto" }
        : { right: "8px", left: "auto" }),
      top: "50%",
      transform: "translateY(-50%)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "18px",
      lineHeight: "1",
      marginTop: "0",
      position: "absolute",
    },
    "& .ant-select-selection-item": {
      ...(dir === "rtl"
        ? { paddingLeft: "24px !important" }
        : { paddingRight: "24px !important" }),
    },
  })
);

const CustomClearIcon = () => (
  <ClearIcon
    sx={{ width: "18px", height: "18px", color: "rgba(0, 0, 0, 0.54)" }}
  />
);

const CustomArrowIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g opacity="0.5">
      <path
        d="M4 6L8 10L12 6"
        stroke="#A3A3A3"
        strokeWidth="1.33333"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
  </svg>
);

const GoMakeTreeSelect = ({
  value,
  onChange,
  options,
  placeholder,
  style,
  selectedTab,
  setSelectedTab,
  isRequired,
  hideOtherTabs,
  hideClear,
  suffixIcon,
  dropdownWidth,
  materialMultiSelect,
  multiValue,
  onMultiChange,
  hideDropdownTabs,
  wideDropdown,
  dropdownSearch,
}: {
  value?: any;
  onChange?: (e: any) => void;
  options: any[];
  placeholder?: string;
  style?: React.CSSProperties;
  selectedTab?: any;
  isRequired?: boolean;
  hideOtherTabs?: boolean;
  hideClear?: boolean;
  /** Replaces the default arrow. Other callers keep the arrow. */
  suffixIcon?: React.ReactNode;
  /** Fixed dropdown width; by default the dropdown matches the control width. */
  dropdownWidth?: number;
  /** Purchase-order materials: checkbox tree with parent = select all under group */
  materialMultiSelect?: boolean;
  multiValue?: string[];
  onMultiChange?: (value: string[]) => void;
  setSelectedTab: (value: any) => void;
  /** Hide the Products / Materials / All tabs rendered above the tree. */
  hideDropdownTabs?: boolean;
  /** Open the popup at twice the trigger width and height. The trigger stays the same size. */
  wideDropdown?: boolean;
  /** Search field inside the popup. The trigger itself stays unchanged. */
  dropdownSearch?: boolean;
}) => {
  const [selectedNodeKey, setSelectedNodeKey] = useState<any>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownQuery, setDropdownQuery] = useState("");
  const [wideDropdownSize, setWideDropdownSize] = useState<{
    width: number;
    maxHeight: number;
  } | null>(null);
  const suppressDropdownCloseRef = useRef(false);

  const treeSelectRef = React.useRef(null);
  const dropdownAnchorRef = React.useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const dir: "rtl" | "ltr" = t("direction");
  const RTL = dir === "rtl";
  const isModern = useTheme().currentTheme === "modern";
  const { classes } = useStyle();

  const GlobalStyle = createGlobalStyle`
        /* The menu is portaled to the page, so a dialog (z-index 1300) covers it. */
        .ant-select-dropdown,
        .ant-tree-select-dropdown {
            z-index: 2000 !important;
        }

        .ant-select-tree-node-content-wrapper {
            width: 100%;
            &:hover {
                background-color: transparent !important;
            }
        }

        .ant-select-tree-title {
            width: 100%;
            display: block;
        }

        .p-tree-container {
            width: 100%;
        }

        .p-treenode {
            padding: ${RTL ? "0px 5px 5px 0px" : "0px 0px 5px 5px"};
        }

        .p-tree .p-tree-container > .p-treenode > .p-treenode-content {
            display: flex;
            justify-content: space-between;
            flex-direction: row-reverse;
        }

        .p-tree .p-tree-container .p-treenode .p-treenode-content .p-tree-toggler {
            transform: ${RTL ? "rotate(180deg)" : "none"};
            max-width: 450px;
        }

        .p-treeselect-panel .p-treeselect-header .p-treeselect-filter-container .p-treeselect-filter-icon {
            position: absolute;
            ${RTL ? "left: 10px; right: unset;" : "right: 10px;"}
            top: 50%;
            transform: translateY(-50%);
        }

        .editorDiv .ant-select-selector {
            border: 1px solid var(--theme-dropdown-border-color, transparent) !important;
        }
        
        .editorDiv .ant-select-focused .ant-select-selector {
            border-color: var(--theme-dropdown-border-color, transparent) !important;
            border-width: 1px !important;
            border-style: solid !important;
            box-shadow: var(--theme-dropdown-shadow, 0 0 0 2px rgba(224, 224, 224, 0.2)) !important;
        }
        
        .editorDiv .ant-select:not(.ant-select-disabled):hover .ant-select-selector {
            border-color: var(--theme-dropdown-border-color, transparent) !important;
            border-width: 1px !important;
            border-style: solid !important;
        }

        .editorDiv .ant-select:not(.ant-select-disabled) .ant-select-selector:has(.ant-select-selection-item) {
            border-color: var(--theme-dropdown-border-color, transparent) !important;
            border-width: 1px !important;
            border-style: solid !important;
        }
    `;

  // const ClearButton = styled(IconButton)(({ dir }) => ({
  //   position: "absolute",
  //   [dir === "rtl" ? "left" : "right"]: "8px",
  //   top: "50%",
  //   transform: "translateY(-50%)",
  //   padding: "4px",
  //   zIndex: 1,
  // }));

  // const handleClear = () => {
  //   setSelectedNodeKey(null);
  //   if (onChange) {
  //     onChange({ value: null });
  //   }
  // };

  // Update selectedNodeKey when value changes
  useEffect(() => {
    if (materialMultiSelect) {
      setSelectedNodeKey(null);
      return;
    }
    if (value?.treeValue || value?.id) {
      setSelectedNodeKey(value.treeValue || value.id);
    } else {
      setSelectedNodeKey(null);
    }
  }, [value, materialMultiSelect]);

  const CustomWidthTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))({
    [`& .${tooltipClasses.tooltip}`]: {
      maxWidth: 600,
    },
  });

  const getLabel = (label) => {
    if (label && label.length > 48) {
      let shortLabel = label.substring(0, 48);
      let lastSpace = shortLabel.lastIndexOf(" ");
      shortLabel = shortLabel.substring(0, lastSpace) + "...";
      return (
        <div>
          <CustomWidthTooltip
            title={label}
            placement="right"
            arrow
            style={{ maxWidth: 500 }}
          >
            <span>{shortLabel}</span>
          </CustomWidthTooltip>
        </div>
      );
    }
    return label;
  };

  const nodeTemplate = (node) => {
    const isFirstLevel = node.level === 0;
    const isSelected = selectedNodeKey === node.value;

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          padding: isSelected && (RTL ? "0px 0px 0px 5px" : "0px 5px 0px 0px"),
        }}
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          {isFirstLevel && <ProductIcon />}
          <span
            style={
              isFirstLevel ? classes.firstLevelLabel : classes.otherLevelsLabel
            }
          >
            {getLabel(node.title)}
          </span>
        </div>
        {isSelected && (
          <CheckIcon
            style={{ width: "20px", height: "20px", color: "var(--theme-primary-color, #2E3092)" }}
          />
        )}
      </div>
    );
  };

  // Recursive function for preparing tree data
  const rec = (option) => {
    if (!option.isProcessed) {
      option.title = nodeTemplate(option);
      option.isProcessed = true;
    }

    if (option.children && option.children.length > 0) {
      for (let i = 0; i < option.children.length; i++) {
        rec(option.children[i]);
      }
    }
  };

  const firstLevelTabs = options.map((item) => ({
    value: item.value,
    title: item.title,
    children: item.children,
  }));

  // Filter tree data based on selected tab
  const filteredTreeData = useMemo(() => {
    const selectedNode = firstLevelTabs.find(
      (item) => Number(item.value) === Number(selectedTab)
    );

    return Number(selectedTab) === ETab.ALL
      ? firstLevelTabs.flatMap((item) => item?.children)
      : selectedNode?.children || [];
  }, [selectedTab, options]);

  const treeDataForRender = useMemo(() => {
    if (materialMultiSelect) {
      return withSelectableForMultiCheck(filteredTreeData);
    }
    return filteredTreeData;
  }, [materialMultiSelect, filteredTreeData]);

  const handleMultiTreeChange: TreeSelectProps<string[]>["onChange"] = (
    next,
  ) => {
    const arr = Array.isArray(next) ? next.map(String) : next != null ? [String(next)] : [];
    suppressDropdownCloseRef.current = true;
    onMultiChange?.(arr);
    setDropdownOpen(true);
  };

  const handleDropdownOpenChange = useCallback(
    (open: boolean) => {
      if (!materialMultiSelect) {
        return;
      }
      if (open) {
        if (wideDropdown) {
          const anchor = dropdownAnchorRef.current;
          const gridItem = anchor?.closest(".MuiGrid-item") as HTMLElement | null;
          const columnWidth = gridItem?.getBoundingClientRect().width;
          if (columnWidth) {
            // Span this filter and the next one. Grid item padding and the
            // inner label box inset the control, so subtract that inset once
            // on each side and the menu lines up with both fields.
            setWideDropdownSize({
              width: Math.round(columnWidth * 2 - 16),
              maxHeight: 800,
            });
          }
        }
        setDropdownOpen(true);
        return;
      }
      if (suppressDropdownCloseRef.current) {
        suppressDropdownCloseRef.current = false;
        setDropdownOpen(true);
        return;
      }
      setDropdownOpen(false);
    },
    [materialMultiSelect, wideDropdown],
  );

  const handleMultiSelect = useCallback(() => {
    if (!materialMultiSelect) {
      return;
    }
    suppressDropdownCloseRef.current = true;
    setDropdownOpen(true);
  }, [materialMultiSelect]);

  useEffect(() => {
    if (!materialMultiSelect) {
      setDropdownOpen(false);
      suppressDropdownCloseRef.current = false;
      return;
    }
    if (!multiValue?.length) {
      setDropdownOpen(false);
      suppressDropdownCloseRef.current = false;
    }
  }, [materialMultiSelect, multiValue]);

  const renderTreeTitle = (node: any) => {
    const label = node?.title;
    if (!isCustomerSkuProduct(node)) {
      return label;
    }
    return (
      <span
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          gap: "8px",
          minWidth: 0,
        }}
      >
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            minWidth: 0,
          }}
        >
          {label}
        </span>
        <span
          style={{
            flexShrink: 0,
            backgroundColor: "#2E7D32",
            color: "#FFFFFF",
            borderRadius: "4px",
            padding: "1px 8px",
            fontSize: "11px",
            fontWeight: 600,
            lineHeight: "18px",
            marginInlineStart: "8px",
            textTransform: "lowercase",
          }}
        >
          {t("customerSkus.sku")}
        </span>
      </span>
    );
  };

  const onPopupScroll: TreeSelectProps["onPopupScroll"] = (e) => { };

  const filterTreeNode = (inputValue, treeNode) => {
    if (!inputValue) return true;
    if (treeNode?.category !== ProductCategoryType.MATERIAL) {
      const searchSource = [
        treeNode.title,
        treeNode.text,
        treeNode.code,
        treeNode.serialNumber,
      ]
        .filter((value) => typeof value === "string")
        .join(" ")
        .toLowerCase();
      return searchSource.includes(inputValue.toLowerCase());
    }
    const searchKeywords = inputValue
      .toString()
      .toLowerCase()
      .split(" ")
      .filter(Boolean);
    const pathTexts = getNodePathText(treeNode);

    let keywordIndex = 0;
    for (let i = 0; i < pathTexts.length; i++) {
      if (pathTexts[i].includes(searchKeywords[keywordIndex])) {
        keywordIndex++;
        if (keywordIndex === searchKeywords.length) {
          return true;
        }
      }
    }
    return false;
  };

  const getNodePathText = (node) => {
    let current = node;
    const pathTexts = [];
    while (current) {
      pathTexts.unshift(current.title?.props?.children || current.title);
      current = current.parent;
    }
    return pathTexts.map((text) =>
      typeof text === "string" ? text.toLowerCase() : ""
    );
  };
  return (
    <div
      ref={dropdownAnchorRef}
      className="editorDiv"
      style={{ position: "relative", width: "100%" }}
    >
      <GlobalStyle />
      <StyledTreeSelect
        className={isModern ? "gm-tree-select" : undefined}
        ref={treeSelectRef}
        key={materialMultiSelect ? "ProductSelectMulti" : "ProductSelect"}
        showSearch
        style={{ width: "100%" }}
        value={materialMultiSelect ? (multiValue ?? []) : (value?.treeValue ?? value?.id)}
        popupMatchSelectWidth={
          dropdownWidth ? dropdownWidth : wideDropdown ? false : undefined
        }
        dropdownStyle={{
          maxHeight: wideDropdown ? wideDropdownSize?.maxHeight ?? 800 : 400,
          overflow: "auto",
          ...(wideDropdown && wideDropdownSize
            ? {
                width: wideDropdownSize.width,
                minWidth: wideDropdownSize.width,
              }
            : dropdownWidth
              ? { width: dropdownWidth, minWidth: dropdownWidth }
              : {}),
        }}
        placeholder={placeholder || t("modal.pleaseSelect")}
        allowClear={!hideClear}
        onChange={materialMultiSelect ? handleMultiTreeChange : onChange}
        open={materialMultiSelect ? dropdownOpen : undefined}
        onOpenChange={(open) => {
          if (materialMultiSelect) handleDropdownOpenChange(open);
          if (!open) setDropdownQuery("");
        }}
        {...(dropdownSearch
          ? { searchValue: dropdownQuery, onSearch: setDropdownQuery }
          : {})}
        onSelect={materialMultiSelect ? handleMultiSelect : undefined}
        autoClearSearchValue={materialMultiSelect ? false : undefined}
        isRequired={isRequired}
        treeData={treeDataForRender}
        treeNodeLabelProp="title"
        fieldNames={{ label: "title", value: "value", children: "children" }}
        treeTitleRender={renderTreeTitle}
        onPopupScroll={onPopupScroll}
        filterTreeNode={filterTreeNode}
        suffixIcon={suffixIcon ?? <CustomArrowIcon />}
        clearIcon={hideClear ? null : <CustomClearIcon />}
        dir={dir}
        hasValue={
          materialMultiSelect
            ? (multiValue?.length ?? 0) > 0
            : !!(value?.treeValue || value?.id)
        }
        treeCheckable={!!materialMultiSelect}
        multiple={!!materialMultiSelect}
        showCheckedStrategy={TreeSelect.SHOW_CHILD}
        maxTagCount="responsive"
        dropdownRender={(menu) => (
          <div>
            {hideDropdownTabs ? null : (
            <UnderlinedTabs
              selectedColor={"primary"}
              value={selectedTab}
              onChange={(e, newValue) => setSelectedTab(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              style={{ marginBottom: "10px" }}
            >
              {hideOtherTabs && selectedTab !== ETab.PRODUCTS ? (
                <></>
              ) : (
                <UnderlinedTab
                  selectedColor={"primary"}
                  key={"products"}
                  label={t("tabs.products")}
                  value={ETab.PRODUCTS}
                />
              )}
              {hideOtherTabs && selectedTab !== ETab.MATERIALS ? (
                <></>
              ) : (
                <UnderlinedTab
                  selectedColor={"primary"}
                  key={"materials"}
                  label={t("tabs.materials")}
                  value={ETab.MATERIALS}
                />
              )}
              {hideOtherTabs && selectedTab !== ETab.ALL ? (
                <></>
              ) : (
                <UnderlinedTab
                  selectedColor={"primary"}
                  key={"all"}
                  label={t("navigationButtons.all")}
                  value={ETab.ALL}
                />
              )}
            </UnderlinedTabs>
            )}
            {dropdownSearch ? (
              <div className="gm-tree-select-search" dir={dir}>
                <input
                  type="text"
                  value={dropdownQuery}
                  autoFocus
                  placeholder={t("header.search")}
                  aria-label={t("header.search")}
                  onChange={(event) => setDropdownQuery(event.target.value)}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    event.currentTarget.focus();
                  }}
                  onKeyDown={(event) => {
                    if (event.key !== "Escape") event.stopPropagation();
                  }}
                />
              </div>
            ) : null}
            {menu}
          </div>
        )}
      />
      {/* {selectedNodeKey && (
        <ClearButton dir={dir} onClick={handleClear}>
          <ClearIcon sx={{ width: "20px", height: "20px" }} />
        </ClearButton>
      )} */}
    </div>
  );
};

export { GoMakeTreeSelect };
